import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

export type ModalSize = "sm" | "md" | "lg" | "xl" | "2xl";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Optional subtitle/description below the title */
  description?: ReactNode;
  /** Shown directly under the header (e.g. API errors) so it stays visible above scrollable body */
  alert?: ReactNode;
  children: ReactNode;
  size?: ModalSize;
  showCloseButton?: boolean;
  /** Extra classes for the scrollable body (e.g. more bottom padding on long forms) */
  bodyClassName?: string;
  /**
   * Precise "has unsaved changes" signal, overriding auto-detection.
   * When omitted, dirtiness is auto-detected from native `input`/`change`
   * events bubbling inside the panel.
   */
  isDirty?: boolean;
  /**
   * Whether a click on the backdrop (outside the panel) may close the modal.
   * When omitted, this is **auto-detected**: a modal that contains editable fields
   * (a form) is never dismissed by an outside click — a stray click must not risk
   * losing input, even before "dirtiness" is detectable (e.g. a value picked from a
   * list emits no native event); a modal with no fields (consultation/detail) keeps
   * click-to-dismiss. Pass an explicit boolean to force either behavior. Deliberate
   * close is always available via ✕ / Escape.
   */
  closeOnBackdrop?: boolean;
}

type CloseSource = "backdrop" | "escape" | "button";

/**
 * Editable controls whose presence marks a modal as a "form" — one whose accidental
 * dismissal (outside click) could lose input. Excludes buttons and hidden/disabled/
 * readonly fields. Consultation modals (none of these) keep click-to-dismiss.
 */
const EDITABLE_FIELD_SELECTOR =
  'input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="reset"]):not([type="image"]):not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled]):not([readonly]), [contenteditable="true"]';

function panelHasEditableFields(panel: HTMLElement | null): boolean {
  return !!panel?.querySelector(EDITABLE_FIELD_SELECTOR);
}

/** Responsive: near full-width on mobile, fixed max-width from sm breakpoint up. */
const sizeStyles: Record<ModalSize, string> = {
  sm: "w-full max-w-[calc(100vw-1rem)] sm:max-w-sm",
  md: "w-full max-w-[calc(100vw-1rem)] sm:max-w-md",
  lg: "w-full max-w-[calc(100vw-1rem)] sm:max-w-xl",
  xl: "w-full max-w-[calc(100vw-1rem)] sm:max-w-2xl",
  "2xl": "w-full max-w-[calc(100vw-1rem)] sm:max-w-4xl",
};

/**
 * Scroll model:
 * - Panel is height-capped (vh + dvh fallback) with flex column.
 * - Body is the primary scroll region (min-h-0 flex-1 overflow-y-auto).
 * - Outer shell keeps overflow-y-auto as a safety net if max-height fails.
 * - z-[60] above BottomNav (z-50).
 */
export function Modal({
  isOpen,
  onClose,
  title,
  description,
  alert,
  children,
  size = "md",
  showCloseButton = true,
  bodyClassName = "",
  isDirty,
  closeOnBackdrop,
}: ModalProps) {
  const { t } = useTranslation("common");
  const panelRef = useRef<HTMLDivElement>(null);
  const keepEditingRef = useRef<HTMLButtonElement>(null);
  const [autoDirty, setAutoDirty] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const dirty = isDirty ?? autoDirty;

  // Never leave a stale dirty flag or confirmation prompt from a previous session.
  useEffect(() => {
    if (isOpen) {
      setAutoDirty(false);
      setShowDiscardConfirm(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const panel = panelRef.current;
    if (!panel) return;
    const markDirty = () => setAutoDirty(true);
    panel.addEventListener("input", markDirty);
    panel.addEventListener("change", markDirty);
    return () => {
      panel.removeEventListener("input", markDirty);
      panel.removeEventListener("change", markDirty);
    };
  }, [isOpen]);

  const requestClose = useCallback(
    (source: CloseSource) => {
      if (source === "backdrop") {
        // "Is this a form?" — explicit `closeOnBackdrop` prop when given, else auto-detected
        // from the presence of editable fields. A form is never dismissed by an outside
        // click (and never prompts): a stray click must not risk losing input, even before
        // dirtiness is detectable (e.g. a value picked from a list emits no native event).
        const allowBackdropClose = closeOnBackdrop ?? !panelHasEditableFields(panelRef.current);
        if (!allowBackdropClose) return;
        // Consultation modal: close when clean; a dirty one still never prompts on backdrop.
        if (dirty) return;
        onClose();
        return;
      }
      // Escape / ✕ are deliberate: close when clean, confirm when dirty.
      if (!dirty) {
        onClose();
        return;
      }
      setShowDiscardConfirm(true);
    },
    [dirty, onClose, closeOnBackdrop]
  );

  // Document-scoped (not Dialog-scoped): dismissing the discard confirm unmounts the
  // focused button, which drops focus to <body> — outside the Dialog's DOM subtree, so a
  // React onKeyDown handler on <Dialog> would silently miss the next Escape press.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // Ignore Escape a nested widget already consumed (native <select>, Headless UI
      // Combobox/Listbox/Menu…) so closing an inner overlay doesn't also prompt discard.
      if (event.defaultPrevented) return;
      // While the discard prompt is up, Escape cancels it (= "keep editing").
      if (showDiscardConfirm) {
        setShowDiscardConfirm(false);
        return;
      }
      requestClose("escape");
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, requestClose, showDiscardConfirm]);

  // A11y: when the discard prompt opens, move focus into it (it's an alertdialog).
  useEffect(() => {
    if (showDiscardConfirm) {
      keepEditingRef.current?.focus();
    }
  }, [showDiscardConfirm]);

  // mousedown (not click) so a text-selection drag ending outside the panel doesn't count.
  const handleDialogMouseDown = (event: ReactMouseEvent<HTMLElement>) => {
    const target = event.target as Node;
    // Dropdowns/popovers portaled to document.body (e.g. the order customer & product
    // pickers) are React descendants of this Dialog, so their mousedown still bubbles to
    // this handler — but their DOM lives OUTSIDE the Dialog. Requiring the target to be
    // physically inside the Dialog (currentTarget) stops a portaled-item click from being
    // mistaken for a backdrop click (which used to close the modal on customer selection).
    if (
      event.currentTarget.contains(target) &&
      panelRef.current &&
      !panelRef.current.contains(target)
    ) {
      requestClose("backdrop");
    }
  };

  const handleDiscard = () => {
    setShowDiscardConfirm(false);
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[60]"
        onClose={() => {
          /* backdrop + Escape are re-implemented via requestClose below */
        }}
        onMouseDown={handleDialogMouseDown}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-black/50 dark:bg-black/70"
            aria-hidden
            data-testid="modal-backdrop"
          />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto overscroll-contain pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] pt-3 sm:pt-4 px-3 sm:px-4">
          <div className="flex min-h-full items-start justify-center sm:items-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel
                ref={panelRef}
                className={`
                  relative flex w-full flex-col overflow-hidden rounded-xl
                  bg-white dark:bg-neutral-800
                  shadow-xl ring-1 ring-neutral-200/50 dark:ring-neutral-700/50
                  transform transition-all
                  max-h-[calc(100vh-1.5rem)] max-h-[calc(100dvh-1.5rem)]
                  sm:max-h-[calc(100vh-2rem)] sm:max-h-[calc(100dvh-2rem)]
                  ${sizeStyles[size]}
                `}
              >
                {(title || description || showCloseButton) && (
                  <div className="shrink-0 px-5 pt-5 pb-4 border-b border-neutral-200 dark:border-neutral-700">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        {title && (
                          <DialogTitle className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                            {title}
                          </DialogTitle>
                        )}
                        {description && (
                          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                            {description}
                          </p>
                        )}
                      </div>
                      {showCloseButton && (
                        <button
                          type="button"
                          onClick={() => requestClose("button")}
                          className="shrink-0 p-2 -m-2 rounded-lg text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                          aria-label={t("close_modal")}
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {alert ? (
                  <div
                    className="shrink-0 px-5 pt-3 pb-3 border-b border-neutral-200 dark:border-neutral-700"
                    role="alert"
                  >
                    {alert}
                  </div>
                ) : null}

                <div
                  className={`min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-5 ${bodyClassName}`.trim()}
                  data-testid="modal-scroll-body"
                >
                  {children}
                </div>

                {showDiscardConfirm && (
                  <div
                    className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 dark:bg-black/60 p-5"
                    role="alertdialog"
                    aria-modal="true"
                    aria-labelledby="modal-discard-confirm-title"
                    data-testid="modal-discard-confirm"
                  >
                    <div className="w-full max-w-sm rounded-lg bg-white dark:bg-neutral-800 p-5 shadow-xl ring-1 ring-neutral-200/50 dark:ring-neutral-700/50">
                      <p
                        id="modal-discard-confirm-title"
                        className="text-base font-semibold text-neutral-900 dark:text-neutral-100"
                      >
                        {t("unsaved_changes_title")}
                      </p>
                      <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                        {t("unsaved_changes_message")}
                      </p>
                      <div className="mt-4 flex justify-end gap-2">
                        <button
                          ref={keepEditingRef}
                          type="button"
                          onClick={() => setShowDiscardConfirm(false)}
                          className="px-3 py-2 rounded-lg text-sm font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          {t("unsaved_changes_keep")}
                        </button>
                        <button
                          type="button"
                          onClick={handleDiscard}
                          className="px-3 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                        >
                          {t("unsaved_changes_discard")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
